import { useState, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Buildings, MapPin, Bed, Bathtub, Users,
  CheckCircle, CurrencyInr, ArrowRight, Warning, NavigationArrow, Spinner,
  IdentificationCard, UploadSimple, CheckFat, Calendar, Star,
  Images, X as XIcon, CaretLeft, CaretRight, ArrowClockwise, Clock, VideoCamera } from "@phosphor-icons/react";
import { DayPicker } from "react-day-picker";
import QRCode from "react-qr-code";
import { eachDayOfInterval, parseISO, addDays as dfAddDays, format, startOfDay, isBefore } from "date-fns";
import "react-day-picker/dist/style.css";

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat",
  "Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
  "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan",
  "Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman and Nicobar Islands","Chandigarh","Delhi","Jammu and Kashmir","Ladakh",
  "Lakshadweep","Puducherry","Dadra and Nagar Haveli and Daman and Diu",
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID || "";

function loadRazorpay() {
  return new Promise(resolve => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const today = () => new Date().toISOString().split("T")[0];
const addDays = (d, n) => {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + n);
  return dt.toISOString().split("T")[0];
};
const nightsBetween = (a, b) => Math.max(Math.floor((new Date(b) - new Date(a)) / 86400000), 0);

export default function BookingPage() {
  const { propertyId } = useParams();
  // No-GST link token. Hosts with the dual-link feature share two links for the same
  // property: the plain one charges GST, this one does not. The server re-validates the
  // token on every call, so nothing here can move the price on its own.
  const noGstToken = useMemo(
    () => new URLSearchParams(window.location.search).get("ng") || "", []);
  const [property, setProperty] = useState(null);
  const [error, setError]       = useState(null);
  const [step, setStep]         = useState("details"); // details | confirm | success

  const [lightboxIdx, setLightboxIdx] = useState(null); // null = closed

  const [range, setRange]       = useState({ from: undefined, to: undefined });
  const [guests, setGuests]     = useState(1);
  const [form, setForm]         = useState({ guest_name: "", guest_email: "", guest_phone: "", guest_state: "",
                                             bill_to_company: "", bill_to_gstin: "", bill_to_address: "" });
  const [corp, setCorp]         = useState(false); // corporate GST invoice requested
  const [booking, setBooking]   = useState(null);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg]           = useState("");
  const [detectingState, setDetectingState] = useState(false);
  const [blockedRanges, setBlockedRanges] = useState([]);
  const [timeLeft, setTimeLeft] = useState(600);
  const [availExpired, setAvailExpired] = useState(false);
  const [holdExpiresAt, setHoldExpiresAt] = useState(null); // ISO — reservation hold expiry
  const [holdTimeLeft, setHoldTimeLeft]   = useState(0);
  const [upiClaimed, setUpiClaimed]       = useState(false);
  const [upiSubmitting, setUpiSubmitting] = useState(false);
  const [earlyCheckin, setEarlyCheckin] = useState(false);
  const [idFile, setIdFile]     = useState(null);
  const [idUrl, setIdUrl]       = useState("");
  const [uploadingId, setUploadingId] = useState(false);
  // IDs for the rest of the party. The lead guest's own ID stays in idUrl above; these
  // are the people staying with them, each with their own name, because the ID record
  // has to name the guest it belongs to.
  const [partyIds, setPartyIds] = useState([]);
  const addPartyId    = () => setPartyIds(p => [...p, { name: "", url: "", fileName: "", uploading: false }]);
  const removePartyId = (i) => setPartyIds(p => p.filter((_, n) => n !== i));
  const patchPartyId  = (i, patch) => setPartyIds(p => p.map((g, n) => (n === i ? { ...g, ...patch } : g)));
  const uploadPartyId = async (i, file) => {
    if (!file) return;
    patchPartyId(i, { uploading: true, fileName: file.name });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/book/upload-id`, { method: "POST", body: fd });
      const data = res.ok ? await res.json() : null;
      patchPartyId(i, { url: data?.url || "", uploading: false });
    } catch {
      patchPartyId(i, { uploading: false });
    }
  };
  const [eventIdFile, setEventIdFile]   = useState(null);
  const [eventIdUrl, setEventIdUrl]     = useState("");
  const [eventUploadingId, setEventUploadingId] = useState(false);
  const leadSavedRef            = useRef(false);

  // Event booking tab
  const [bookingTab, setBookingTab]   = useState("stay"); // "stay" | "event"
  const [eventForm, setEventForm]     = useState({
    event_type: "", event_start_date: "", event_end_date: "",
    attendees: "", organizer_name: "", organizer_email: "", organizer_phone: "", notes: "",
  });
  const [eventMsg, setEventMsg]       = useState("");
  const [eventSubmitting, setEventSubmitting] = useState(false);
  const [eventSuccess, setEventSuccess] = useState(false);

  const checkIn  = range?.from ? format(range.from, "yyyy-MM-dd") : "";
  const checkOut = range?.to   ? format(range.to,   "yyyy-MM-dd") : "";

  const expandRanges = (ranges) => ranges.flatMap(r => {
    try {
      const start = parseISO(r.check_in);
      const end   = dfAddDays(parseISO(r.check_out), -1);
      if (isBefore(end, start)) return [];
      return eachDayOfInterval({ start, end });
    } catch { return []; }
  });

  // Ranges typed "buffer" are the host's turnover gap around a stay; untyped
  // ranges (older API responses) are treated as bookings.
  const bookedDates = useMemo(
    () => expandRanges(blockedRanges.filter(r => !r.type || r.type === "booking" || r.type === "cutoff")),
    [blockedRanges]
  );
  const bufferDates = useMemo(() => {
    const bookedSet = new Set(bookedDates.map(d => format(d, "yyyy-MM-dd")));
    return expandRanges(blockedRanges.filter(r => r.type === "buffer"))
      .filter(d => !bookedSet.has(format(d, "yyyy-MM-dd")));
  }, [blockedRanges, bookedDates]);

  const detectState = () => {
    if (!navigator.geolocation) return;
    setDetectingState(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${coords.latitude}&lon=${coords.longitude}&format=json`,
            { headers: { "Accept-Language": "en" } }
          );
          const data = await res.json();
          const raw = data.address?.state || "";
          const matched = INDIAN_STATES.find(s => s.toLowerCase() === raw.toLowerCase()) || raw;
          if (matched) setForm(f => ({ ...f, guest_state: matched }));
        } catch { /* silent */ }
        setDetectingState(false);
      },
      () => setDetectingState(false),
      { timeout: 8000 }
    );
  };

  const refreshAvailability = () => {
    fetch(`${API}/book/property/${propertyId}/availability`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        setBlockedRanges(data);
        setTimeLeft(600);
        setAvailExpired(false);
      })
      .catch(() => {});
  };

  useEffect(() => {
    const q = noGstToken ? `?ng=${encodeURIComponent(noGstToken)}` : "";
    fetch(`${API}/book/property/${propertyId}${q}`)
      .then(r => r.ok ? r.json() : Promise.reject("Not found"))
      .then(setProperty)
      .catch(() => setError("This property is not available for direct booking."));
    fetch(`${API}/book/property/${propertyId}/availability`)
      .then(r => r.ok ? r.json() : [])
      .then(setBlockedRanges)
      .catch(() => {});
  }, [propertyId, noGstToken]);

  // On-demand iCal sync when guest selects dates — closes the 15-min polling gap
  useEffect(() => {
    if (!checkIn || !checkOut) return;
    fetch(`${API}/book/property/${propertyId}/refresh-availability`, { method: "POST" }).catch(() => {});
  }, [checkIn, checkOut, propertyId]);

  useEffect(() => {
    if (availExpired) return;
    const id = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { setAvailExpired(true); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [availExpired]);

  // Reservation hold countdown — starts when /reserve succeeds
  useEffect(() => {
    if (!holdExpiresAt) return;
    const tick = () => {
      const secs = Math.floor((new Date(holdExpiresAt) - new Date()) / 1000);
      if (secs <= 0) {
        setHoldTimeLeft(0);
        setBooking(null);
        setHoldExpiresAt(null);
        setStep("details");
        setMsg("Your 15-minute reservation hold expired. Please try again.");
      } else {
        setHoldTimeLeft(secs);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [holdExpiresAt]);

  const nights = nightsBetween(checkIn, checkOut);
  const total  = (property?.nightly_rate || 0) * nights;

  // The server decides whether GST applies at all — it knows whether this host holds a
  // GSTIN and which of their two booking links was opened. The slab below is only a
  // fallback for an older backend build that doesn't send gst_pct yet.
  const slabRate  = property?.nightly_rate > 7500 ? 0.18 : property?.nightly_rate >= 1000 ? 0.05 : 0;
  const gstRate   = property?.gst_applicable === false ? 0
                  : (property?.gst_pct != null ? property.gst_pct / 100 : slabRate);
  // Supply type only picks the LABEL (CGST+SGST vs IGST). It must not gate the amount:
  // the backend defaults to intrastate when the guest leaves state blank, so gating on it
  // made the page quote a GST-free total and then charge GST at Razorpay.
  const gstType   = form.guest_state && property?.state
    ? (form.guest_state.toLowerCase() === (property.state || "").toLowerCase() ? "intrastate" : "interstate")
    : "intrastate";
  const gstAmount = gstRate > 0 ? Math.round(total * gstRate) : 0;
  const grandTotal = total + gstAmount;

  // Dynamic UPI deep link / QR payload — amount + booking ref pre-filled so the
  // guest can't pay the wrong amount. Only when the host has a UPI ID on file.
  // Honour upi_only only when the host actually has UPI details. The backend applies the
  // same guard; doing it here too means a stale response can never hide the card button
  // AND the UPI block at once, which would leave the guest no way to pay.
  const hasUpi = Boolean(property?.host_upi_id || property?.host_upi_qr_url);
  const upiOnly = Boolean(property?.host_upi_only) && hasUpi;

  const upiUri = property?.host_upi_id
    ? `upi://pay?pa=${encodeURIComponent(property.host_upi_id)}&pn=${encodeURIComponent((property.host_name || property.name || "Host").slice(0, 40))}&am=${grandTotal.toFixed(2)}&cu=INR&tn=${encodeURIComponent("HF" + (booking?.booking_id?.slice(0, 8) || "booking"))}`
    : "";

  const saveLead = (source = "form_blur") => {
    if (!form.guest_email && !form.guest_phone) return;
    fetch(`${API}/book/lead`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...form, property_id: propertyId,
        check_in: checkIn, check_out: checkOut,
        nights, amount: grandTotal, source,
      }),
    }).catch(() => {});
  };

  const handleIdUpload = async (file) => {
    if (!file) return;
    setIdFile(file);
    setUploadingId(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/book/upload-id`, { method: "POST", body: fd });
      if (res.ok) {
        const data = await res.json();
        setIdUrl(data.url || "");
      }
    } catch { /* non-blocking */ }
    setUploadingId(false);
  };

  const handleEventIdUpload = async (file) => {
    if (!file) return;
    setEventIdFile(file);
    setEventUploadingId(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/book/upload-id`, { method: "POST", body: fd });
      if (res.ok) {
        const data = await res.json();
        setEventIdUrl(data.url || "");
      }
    } catch { /* non-blocking */ }
    setEventUploadingId(false);
  };

  const handleProceed = async () => {
    if (!form.guest_name.trim()) { setMsg("Please enter your name"); return; }
    if (!form.guest_email.trim()) { setMsg("Please enter your email"); return; }
    if (!form.guest_phone.trim()) { setMsg("Please enter your phone number"); return; }
    if (!range?.from || !range?.to) { setMsg("Please select check-in and check-out dates"); return; }
    if (nights < 1) { setMsg("Check-out must be after check-in"); return; }
    if (!idUrl) { setMsg("Please upload a government ID (Aadhaar / Passport / Driver's License)"); return; }
    if (corp && form.bill_to_gstin && form.bill_to_gstin.replace(/\s/g, "").length !== 15) {
      setMsg("Company GSTIN must be 15 characters. Leave it blank if you don't have one."); return;
    }
    setMsg("");
    setSubmitting(true);
    try {
      // Reserve now — creates 15-min hold + Aiosell OTA block, server checks availability definitively
      const res = await fetch(`${API}/book/property/${propertyId}/reserve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, check_in: checkIn, check_out: checkOut, guests, guest_state: form.guest_state, guest_id_url: idUrl, early_checkin_requested: earlyCheckin,
          guest_ids: partyIds.filter(g => g.url && g.name.trim()).map(g => ({ name: g.name.trim(), url: g.url })),
          no_gst_token: noGstToken,
          bill_to_company: corp ? form.bill_to_company.trim() : "",
          bill_to_gstin:   corp ? form.bill_to_gstin.trim().toUpperCase() : "",
          bill_to_address: corp ? form.bill_to_address.trim() : "" }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Booking failed"); }
      const data = await res.json();
      setBooking(data);
      setRequiresApproval(!!data.requires_approval);
      if (data.reservation_expires_at) setHoldExpiresAt(data.reservation_expires_at);
      saveLead("proceed_to_payment");
      if (data.razorpay_order_id) {
        setStep("review");
      } else {
        setStep("success");
      }
    } catch (err) {
      setMsg(err.message || "Booking failed. Please try again.");
    }
    setSubmitting(false);
  };

  const cancelHold = (bookingData) => {
    if (!bookingData?.booking_id) return;
    fetch(`${API}/book/property/${propertyId}/cancel-hold`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ booking_id: bookingData.booking_id }),
    }).catch(() => {});
    setBooking(null);
    setHoldExpiresAt(null);
    setHoldTimeLeft(0);
  };

  const handleReserve = async () => {
    // Hold already created in handleProceed — just open Razorpay
    if (!booking) { setMsg("Session expired. Please go back and try again."); return; }
    setMsg("");
    await initiatePayment(booking);
  };

  // Open a booking invoice PDF in a new tab. kind: "proforma" (pre-payment) | "tax" (paid).
  const openInvoice = (kind) => {
    const bid = booking?.booking_id;
    if (!bid) return;
    const path = kind === "tax" ? "invoice" : "proforma";
    window.open(`${API}/book/booking/${bid}/${path}`, "_blank", "noopener");
  };

  const handleUpiPaid = async () => {
    if (!booking?.booking_id) { setMsg("Session expired. Please go back and try again."); return; }
    setMsg("");
    setUpiSubmitting(true);
    try {
      const res = await fetch(`${API}/book/property/${propertyId}/upi-paid`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ booking_id: booking.booking_id, claimed_amount: grandTotal }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        if (res.status === 410) {
          setHoldExpiresAt(null);
          setBooking(null);
          setStep("details");
          setMsg(e.detail || "Your reservation expired. Please start again.");
        } else {
          setMsg(e.detail || "Could not submit — please try again.");
        }
        return;
      }
      setUpiClaimed(true);
      setRequiresApproval(true);
      setHoldExpiresAt(null);
      setStep("success");
    } catch {
      setMsg("Could not submit — please try again.");
    } finally {
      setUpiSubmitting(false);
    }
  };

  const initiatePayment = async (bookingData) => {
    const loaded = await loadRazorpay();
    if (!loaded) { setMsg("Payment gateway failed to load. Please retry."); return; }

    const keyId = bookingData.razorpay_key_id || RAZORPAY_KEY;
    const options = {
      key: keyId,
      amount: Math.round(grandTotal * 100),
      currency: "INR",
      order_id: bookingData.razorpay_order_id,
      name: property.host_name ? `${property.host_name} via HostFolio` : property.name,
      description: `${property.name} · ${nights} night${nights > 1 ? "s" : ""} — ${checkIn} to ${checkOut}`,
      prefill: { name: form.guest_name, email: form.guest_email, contact: form.guest_phone },
      theme: { color: "#f59e0b" },
      handler: async (response) => {
        const confirmRes = await fetch(`${API}/book/property/${propertyId}/confirm-payment`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            booking_id: bookingData.booking_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          }),
        });
        if (!confirmRes.ok) {
          const errData = await confirmRes.json().catch(() => ({}));
          if (confirmRes.status === 410) {
            setHoldExpiresAt(null);
            setBooking(null);
            setStep("details");
            setMsg(errData.detail || "Your reservation expired. Please start again.");
          } else {
            setMsg("Payment received but verification failed. Please contact support.");
          }
          return;
        }
        setHoldExpiresAt(null);
        setStep("success");
      },
      modal: { ondismiss: () => { cancelHold(bookingData); setMsg("Payment cancelled — your hold has been released. You can book again."); } },
    };
    const rz = new window.Razorpay(options);
    rz.open();
  };

  const eventPackagePrice = (() => {
    if (!eventForm.event_type || !property?.event_pricing) return 0;
    return property.event_pricing[eventForm.event_type] || 0;
  })();

  const handleEventSubmit = async () => {
    if (!eventForm.organizer_name.trim()) { setEventMsg("Please enter your name"); return; }
    if (!eventForm.organizer_email.trim()) { setEventMsg("Please enter your email"); return; }
    if (!eventForm.organizer_phone.trim()) { setEventMsg("Please enter your phone number"); return; }
    if (!eventForm.event_start_date) { setEventMsg("Please select an event start date"); return; }
    if (!eventForm.event_type) { setEventMsg("Please select an event type"); return; }
    const endDate = eventForm.event_end_date || eventForm.event_start_date;
    if (endDate < eventForm.event_start_date) { setEventMsg("End date cannot be before start date"); return; }
    if (!eventIdUrl) { setEventMsg("Please upload a government ID (Aadhaar / Passport / Driver's License)"); return; }
    setEventMsg("");
    setEventSubmitting(true);
    try {
      const res = await fetch(`${API}/book/property/${propertyId}/reserve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          booking_type:      "event",
          event_type:        eventForm.event_type,
          event_start_date:  eventForm.event_start_date,
          event_end_date:    endDate,
          event_details: { attendees: Number(eventForm.attendees) || 0, notes: eventForm.notes },
          guest_name:   eventForm.organizer_name,
          guest_email:  eventForm.organizer_email,
          guest_phone:  eventForm.organizer_phone,
          guest_id_url: eventIdUrl,
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Submission failed"); }
      setEventSuccess(true);
    } catch (err) {
      setEventMsg(err.message || "Submission failed. Please try again.");
    }
    setEventSubmitting(false);
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4">
      <div className="text-center">
        <Warning size={48} className="mx-auto mb-4 text-amber-400" />
        <p className="text-slate-700 text-lg font-semibold">{error}</p>
        <p className="text-slate-400 text-sm mt-2">Contact the host for more information.</p>
      </div>
    </div>
  );

  if (!property) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
      <div className="w-8 h-8 border-3 border-amber-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (step === "success") return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4">
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl p-10 max-w-md w-full text-center">
        <div className={`w-16 h-16 ${requiresApproval ? "bg-amber-50" : "bg-green-50"} rounded-full flex items-center justify-center mx-auto mb-4`}>
          <CheckCircle size={36} weight="fill" className={requiresApproval ? "text-amber-500" : "text-green-500"} />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">
          {upiClaimed ? "Payment Submitted!" : requiresApproval ? "Booking Requested!" : "Booking Confirmed!"}
        </h2>
        <p className="text-slate-300 dark:text-slate-200 text-sm mb-6">
          {upiClaimed
            ? <>Thank you, {form.guest_name}! We've notified the host about your UPI payment for <strong>{property.name}</strong>. They'll verify it and confirm your booking shortly.</>
            : requiresApproval
            ? <>Thank you, {form.guest_name}! Your request for <strong>{property.name}</strong> has been sent. The host will confirm shortly.</>
            : <>Thank you, {form.guest_name}! Your stay at <strong>{property.name}</strong> is confirmed.</>
          }
        </p>
        {requiresApproval && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 text-xs text-amber-700 mb-4 text-left">
            <Warning size={13} weight="fill" className="shrink-0" />
            {upiClaimed
              ? "Your dates are held while the host verifies your UPI payment. You'll receive a confirmation email once approved."
              : "Your dates are held while the host reviews. You'll receive a confirmation email once approved."}
          </div>
        )}
        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-2xl p-4 text-sm text-left space-y-2 mb-6">
          <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Check-in</span><span className="font-semibold dark:text-white">{checkIn}</span></div>
          <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Check-out</span><span className="font-semibold dark:text-white">{checkOut}</span></div>
          <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Nights</span><span className="font-semibold dark:text-white">{nights}</span></div>
          <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">{upiClaimed ? "Paid via UPI (being verified)" : "Total Paid"}</span><span className="font-bold text-amber-600">₹{grandTotal.toLocaleString("en-IN")}</span></div>
        </div>
        <button type="button"
          onClick={() => openInvoice(!requiresApproval && !upiClaimed ? "tax" : "proforma")}
          className="w-full mb-4 py-2.5 rounded-2xl border border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition">
          🧾 {!requiresApproval && !upiClaimed
                ? (gstRate > 0 ? "Download GST tax invoice" : "Download invoice")
                : "Download proforma invoice"}
        </button>
        <p className="text-xs text-slate-400">A confirmation will be sent to {form.guest_email}. Booking ID: {booking?.booking_id?.slice(0,8)}</p>
        <div className="mt-6 pt-4 border-t border-slate-100">
          <a href="https://hostfolio.in" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-amber-500 transition">
            Powered by <span className="font-bold ml-1"><span className="text-slate-400">Host</span><span className="text-amber-500">Folio</span></span>
          </a>
        </div>
      </motion.div>
    </div>
  );

  if (step === "review") return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <header className="bg-white border-b border-slate-100 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          <span className="font-manrope text-lg font-bold"><span className="text-slate-800">Host</span><span className="text-amber-500">Folio</span></span>
          <span className="text-slate-300 mx-1">·</span>
          <span className="text-sm text-slate-300 truncate">{property.name}</span>
        </div>
      </header>
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-5">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 space-y-4">
          <h2 className="font-semibold text-slate-800 text-base">Review Your Booking</h2>
          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 space-y-2 text-sm">
            <div className="flex justify-between text-slate-700"><span className="text-slate-500">Property</span><span className="font-semibold">{property.name}</span></div>
            {property.host_name && (
              <div className="flex justify-between text-slate-700"><span className="text-slate-500">Hosted by</span><span className="font-semibold">{property.host_name}</span></div>
            )}
            <div className="flex justify-between text-slate-700"><span className="text-slate-500">Check-in</span><span className="font-semibold">{checkIn}</span></div>
            <div className="flex justify-between text-slate-700"><span className="text-slate-500">Check-out</span><span className="font-semibold">{checkOut}</span></div>
            <div className="flex justify-between text-slate-700"><span className="text-slate-500">Nights</span><span className="font-semibold">{nights}</span></div>
            <div className="flex justify-between text-slate-700"><span className="text-slate-500">Guests</span><span className="font-semibold">{guests}</span></div>
            <div className="flex justify-between text-slate-700 text-xs"><span className="text-slate-400">₹{(property.nightly_rate||0).toLocaleString("en-IN")} × {nights} nights</span><span>₹{total.toLocaleString("en-IN")}</span></div>
            {gstType && gstRate > 0 && (
              <div className="flex justify-between text-slate-500 text-xs">
                <span>{gstType === "intrastate" ? `CGST+SGST (${gstRate*100}%)` : `IGST (${gstRate*100}%)`}</span>
                <span>₹{gstAmount.toLocaleString("en-IN")}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-slate-800 border-t border-amber-100 pt-2 text-base">
              <span>Total</span><span className="text-amber-600">₹{grandTotal.toLocaleString("en-IN")}</span>
            </div>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 space-y-1.5 text-sm">
            <p className="text-xs font-semibold text-slate-400 mb-2">YOUR DETAILS</p>
            <div className="flex justify-between"><span className="text-slate-500">Name</span><span className="font-medium text-slate-800">{form.guest_name}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="font-medium text-slate-800">{form.guest_email}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Phone</span><span className="font-medium text-slate-800">{form.guest_phone}</span></div>
          </div>
          {msg && (
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
              <Warning size={13} weight="fill" />{msg}
            </div>
          )}

          {/* ── Reservation hold countdown ── */}
          {holdExpiresAt && holdTimeLeft > 0 && (
            <div className={`flex items-center gap-2 text-xs rounded-xl px-3 py-2 ${
              holdTimeLeft <= 120
                ? "bg-red-50 border border-red-100 text-red-600"
                : "bg-amber-50 border border-amber-100 text-amber-700"
            }`}>
              <Clock size={13} weight="bold" />
              <span>
                Dates held for&nbsp;
                <strong>{Math.floor(holdTimeLeft / 60)}:{String(holdTimeLeft % 60).padStart(2, "0")}</strong>
                &nbsp;— complete payment before the timer expires
              </span>
            </div>
          )}

          {/* ── UPI / QR (primary payment option) ── */}
          {(upiUri || property.host_upi_qr_url) && (
            <div className="border border-slate-200 rounded-2xl p-4 space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Pay directly via UPI</p>
              {upiUri ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="bg-white rounded-xl border border-slate-100 p-3">
                    <QRCode value={upiUri} size={168} />
                  </div>
                  <p className="text-[11px] text-slate-400 text-center">
                    Scan with any UPI app — PhonePe, GPay, Paytm, etc. Amount of ₹{grandTotal.toLocaleString("en-IN")} is pre-filled.
                  </p>
                  <a href={upiUri} className="text-xs font-semibold text-amber-600 hover:text-amber-700">
                    On your phone? Tap to open your UPI app →
                  </a>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <img
                    src={property.host_upi_qr_url}
                    alt="UPI QR Code"
                    className="w-48 h-48 object-contain rounded-xl border border-slate-100 bg-white p-2"
                  />
                  <p className="text-[11px] text-slate-400 text-center">Scan with any UPI app and pay exactly ₹{grandTotal.toLocaleString("en-IN")}.</p>
                </div>
              )}
              {property.host_upi_id && (
                <div className="flex items-center justify-between gap-3 bg-slate-50 rounded-xl px-3 py-2.5">
                  <span className="font-mono text-sm font-semibold text-slate-800">{property.host_upi_id}</span>
                  <button
                    onClick={() => { navigator.clipboard.writeText(property.host_upi_id); }}
                    className="text-xs text-amber-600 hover:text-amber-700 font-semibold shrink-0"
                  >
                    Copy UPI ID
                  </button>
                </div>
              )}
              <button onClick={handleUpiPaid} disabled={upiSubmitting}
                className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm rounded-2xl transition disabled:opacity-50 flex items-center justify-center gap-2">
                {upiSubmitting
                  ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  : <>I've paid ₹{grandTotal.toLocaleString("en-IN")} via UPI</>}
              </button>
              <p className="text-[11px] text-slate-400">
                After you pay, tap the button above — the host will verify the payment in their UPI app and confirm your booking.
              </p>
            </div>
          )}

          {/* ── Divider ── */}
          {hasUpi && !upiOnly && (
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs text-slate-400 font-medium">or pay by card</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
          )}

          {/* ── Razorpay (card / net banking) ──
              Hidden when the host collects by UPI only: those payments settle to the
              platform gateway, not the host, so offering the card route would send the
              money to the wrong account. ── */}
          {!upiOnly && (
          <button onClick={handleReserve} disabled={submitting}
            className="w-full py-3.5 bg-amber-400 hover:bg-amber-500 text-white font-bold text-sm rounded-2xl transition disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm">
            {submitting
              ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              : <><CurrencyInr size={16} weight="bold" />
                {(property.host_upi_qr_url || property.host_upi_id)
                  ? `Pay by Card / Net Banking ₹${grandTotal.toLocaleString("en-IN")}`
                  : (grandTotal > 0 ? `Confirm & Pay ₹${grandTotal.toLocaleString("en-IN")}` : "Confirm Booking")}
                <ArrowRight size={14} weight="bold" /></>
            }
          </button>
          )}

          <button type="button" onClick={() => openInvoice("proforma")}
            className="w-full py-2 text-xs font-semibold text-slate-500 hover:text-amber-600 transition">
            🧾 Download proforma invoice{gstRate > 0 ? " (GST)" : ""}{form.bill_to_company ? ` — ${form.bill_to_company}` : ""}
          </button>

          <button onClick={() => { cancelHold(booking); setStep("details"); }} className="w-full py-2 text-sm text-slate-500 hover:text-slate-700 transition">
            ← Back to edit details
          </button>
        </motion.div>
        <p className="text-center text-xs text-slate-400">Dates are held for 15 minutes from when you proceed to review.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-100 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          <span className="font-manrope text-lg font-bold"><span className="text-slate-800 dark:text-slate-100">Host</span><span className="text-amber-500">Folio</span></span>
          <span className="text-slate-300 mx-1">·</span>
          <span className="text-sm text-slate-300 truncate">{property.name}</span>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Lightbox */}
        {lightboxIdx !== null && property.photos?.length > 0 && (
          <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={() => setLightboxIdx(null)}>
            <button onClick={() => setLightboxIdx(null)}
              className="absolute top-4 right-4 text-white/70 hover:text-white p-2">
              <XIcon size={24} weight="bold" />
            </button>
            <button
              onClick={e => { e.stopPropagation(); setLightboxIdx(i => Math.max(0, i - 1)); }}
              disabled={lightboxIdx === 0}
              className="absolute left-3 text-white/70 hover:text-white p-3 disabled:opacity-20">
              <CaretLeft size={24} weight="bold" />
            </button>
            <img
              src={property.photos[lightboxIdx]}
              alt={`${property.name} ${lightboxIdx + 1}`}
              className="max-h-[90vh] max-w-[90vw] object-contain rounded-xl"
              onClick={e => e.stopPropagation()}
            />
            <button
              onClick={e => { e.stopPropagation(); setLightboxIdx(i => Math.min(property.photos.length - 1, i + 1)); }}
              disabled={lightboxIdx === property.photos.length - 1}
              className="absolute right-3 text-white/70 hover:text-white p-3 disabled:opacity-20">
              <CaretRight size={24} weight="bold" />
            </button>
            <p className="absolute bottom-4 text-white/50 text-sm">
              {lightboxIdx + 1} / {property.photos.length}
            </p>
          </div>
        )}

        {/* Property card */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
          {property.photos?.length > 0 ? (
            <>
              {/* Hero photo */}
              <div className="relative cursor-pointer" onClick={() => setLightboxIdx(0)}>
                <img src={property.photos[0]} alt={property.name}
                  className="w-full h-56 object-cover" />
                {property.photos.length > 1 && (
                  <span className="absolute bottom-2 right-2 bg-black/60 text-white text-[11px] font-semibold px-2 py-1 rounded-full flex items-center gap-1">
                    <Images size={11} weight="fill" /> {property.photos.length} photos
                  </span>
                )}
              </div>
              {/* Thumbnail strip */}
              {property.photos.length > 1 && (
                <div className="flex gap-1.5 overflow-x-auto px-3 py-2 scrollbar-hide">
                  {property.photos.slice(1).map((src, i) => (
                    <img key={i} src={src} alt={`photo ${i + 2}`}
                      onClick={() => setLightboxIdx(i + 1)}
                      className="h-16 w-24 object-cover rounded-lg shrink-0 cursor-pointer opacity-80 hover:opacity-100 transition" />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-32 bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
              <Buildings size={48} weight="duotone" className="text-amber-300" />
            </div>
          )}
          {property.videos?.length > 0 && (
            <div className="px-3 pt-2 pb-1">
              <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-300 flex items-center gap-1 mb-1.5">
                <VideoCamera size={12} weight="fill" className="text-amber-400" /> Video tour
              </p>
              <div className={property.videos.length > 1 ? "flex gap-2 overflow-x-auto scrollbar-hide" : ""}>
                {property.videos.map((src, i) => (
                  <video key={i} src={src} controls preload="metadata" playsInline
                    className={"rounded-xl bg-black " + (property.videos.length > 1 ? "h-40 shrink-0" : "w-full max-h-64")} />
                ))}
              </div>
            </div>
          )}
          <div className="p-5">
            <h1 className="text-xl font-bold text-slate-800 dark:text-white mb-1">{property.name}</h1>
            {property.location && (
              <p className="text-sm text-slate-300 flex items-center gap-1 mb-3">
                <MapPin size={13} weight="fill" className="text-amber-400" />{property.location}
              </p>
            )}
            <div className="flex gap-4 text-sm text-slate-300">
              <span className="flex items-center gap-1"><Bed size={14} />{property.bedrooms} BR</span>
              {property.bathrooms > 0 && <span className="flex items-center gap-1"><Bathtub size={14} />{property.bathrooms} BA</span>}
              <span className="flex items-center gap-1"><Users size={14} />Up to {property.max_guests} guests</span>
            </div>
            {property.host_name && (
              <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                <IdentificationCard size={13} className="text-amber-400 shrink-0" />
                Hosted by <span className="font-semibold text-slate-600 dark:text-slate-300 ml-1">{property.host_name}</span>
              </p>
            )}
            {(property.amenities?.length > 0 || property.extra_amenities?.length > 0) && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {[...(property.amenities || []), ...(property.extra_amenities || [])].slice(0, 12).map(a => (
                  <span key={a} className="text-[11px] bg-slate-50 dark:bg-slate-700 border border-slate-100 dark:border-slate-600 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded-full">{a}</span>
                ))}
                {((property.amenities?.length || 0) + (property.extra_amenities?.length || 0)) > 12 && (
                  <span className="text-[11px] text-slate-400 px-1 py-0.5">+{(property.amenities?.length || 0) + (property.extra_amenities?.length || 0) - 12} more</span>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Booking form */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white dark:bg-slate-800 rounded-3xl shadow-sm border border-slate-100 overflow-hidden">

          {/* Tab bar — only shown if event hosting is enabled */}
          {property.event_hosting_enabled && (
            <div className="flex border-b border-slate-100">
              <button
                onClick={() => setBookingTab("stay")}
                className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-semibold transition ${
                  bookingTab === "stay"
                    ? "text-amber-600 border-b-2 border-amber-400 bg-amber-50/50"
                    : "text-slate-400 hover:text-slate-600"
                }`}
              >
                <Bed size={15} weight={bookingTab === "stay" ? "fill" : "regular"} />
                Book a Stay
              </button>
              <button
                onClick={() => setBookingTab("event")}
                className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-semibold transition ${
                  bookingTab === "event"
                    ? "text-amber-600 border-b-2 border-amber-400 bg-amber-50/50"
                    : "text-slate-400 hover:text-slate-600"
                }`}
              >
                <Star size={15} weight={bookingTab === "event" ? "fill" : "regular"} />
                Book for an Event
              </button>
            </div>
          )}

          {/* ── Stay booking form ── */}
          {bookingTab === "stay" && (
          <div className="p-6 space-y-5">
          <h2 className="font-semibold text-slate-800 dark:text-white text-base">Book Your Stay</h2>

          {/* Date Picker Calendar */}
          <div>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">SELECT YOUR DATES</p>
            {availExpired && (
              <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Warning size={13} weight="fill" className="text-amber-500 shrink-0" />
                  <span className="text-[11px] text-amber-800 font-medium">Availability may have changed — please refresh</span>
                </div>
                <button
                  onClick={refreshAvailability}
                  className="flex items-center gap-1 text-[11px] font-semibold text-amber-600 hover:text-amber-700 bg-amber-100 hover:bg-amber-200 px-2 py-1 rounded-lg transition shrink-0"
                >
                  <ArrowClockwise size={11} weight="bold" />
                  Refresh
                </button>
              </div>
            )}
            <div className="flex justify-center rounded-2xl border border-slate-200 dark:border-slate-600 overflow-hidden">
              <DayPicker
                mode="range"
                selected={range}
                onSelect={setRange}
                disabled={[{ before: startOfDay(new Date()) }, ...bookedDates, ...bufferDates]}
                modifiers={{ booked: bookedDates, buffer: bufferDates }}
                modifiersStyles={{
                  booked: { textDecoration: "line-through", color: "#ef4444", opacity: 0.55, cursor: "not-allowed" },
                  buffer: { color: "#1d4ed8", backgroundColor: "#dbeafe", opacity: 0.65, borderRadius: "6px", cursor: "not-allowed" },
                  selected: { backgroundColor: "#f59e0b", color: "#fff", borderRadius: "6px" },
                  range_middle: { backgroundColor: "#fef3c7", color: "#92400e" },
                }}
                styles={{ root: { margin: 0 } }}
              />
            </div>
            {bookedDates.length > 0 && (
              <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1">
                <span className="inline-block w-3 h-px bg-red-400 relative"><span className="absolute inset-0 flex items-center justify-center text-red-400 text-[9px]">—</span></span>
                Crossed-out dates are already booked
              </p>
            )}
            {bufferDates.length > 0 && (
              <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded bg-blue-100 border border-blue-300 shrink-0" />
                Blue dates are preparation days — the host needs time to get the place ready between stays
              </p>
            )}
            {(property.checkin_time || property.checkout_time) && (() => {
              // Same-day turnover allowed only when the host has no day gap AND
              // the standard checkout→check-in window meets their minimum hours
              // (mirrors the backend's effective-buffer rule).
              const toMins = (t, dflt) => { const [h, m] = String(t || dflt).split(":"); return (parseInt(h) || 0) * 60 + (parseInt(m) || 0); };
              const minH = property.min_turnover_hours ?? 0;
              const gapOk = minH === 0 || (toMins(property.checkin_time, "14:00") - toMins(property.checkout_time, "11:00")) >= minH * 60;
              const sameDayOk = (property.buffer_days ?? 0) === 0 && gapOk;
              return (
                <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                  <Clock size={11} className="shrink-0" />
                  Check-in from {property.checkin_time || "14:00"} · Checkout by {property.checkout_time || "11:00"}
                  {sameDayOk ? " — checkout days are open for same-day check-in" : ""}
                </p>
              );
            })()}
            {range?.from && range?.to && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div className="bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                  <p className="text-[10px] text-amber-600 font-semibold uppercase">Check-in</p>
                  <p className="font-semibold text-slate-800 text-sm">{checkIn}</p>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                  <p className="text-[10px] text-amber-600 font-semibold uppercase">Check-out</p>
                  <p className="font-semibold text-slate-800 text-sm">{checkOut}</p>
                </div>
              </div>
            )}
            {!range?.from && (
              <p className="text-xs text-slate-400 text-center mt-2">Click a start date, then an end date</p>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block">GUESTS</label>
            <select value={guests} onChange={e => setGuests(Number(e.target.value))}
              className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-100 focus:outline-none focus:border-amber-400">
              {[...Array(property.max_guests || 6)].map((_, i) => (
                <option key={i+1} value={i+1}>{i+1} guest{i > 0 ? "s" : ""}</option>
              ))}
            </select>
          </div>

          {/* Price summary */}
          {nights > 0 && (
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 space-y-2 text-sm">
              <div className="flex justify-between text-slate-600 dark:text-slate-200">
                <span>₹{(property.nightly_rate || 0).toLocaleString("en-IN")} × {nights} night{nights > 1 ? "s" : ""}</span>
                <span>₹{total.toLocaleString("en-IN")}</span>
              </div>
              {gstType && gstRate > 0 && (
                <div className="flex justify-between text-slate-500 dark:text-slate-300 text-xs">
                  <span>
                    {gstType === "intrastate"
                      ? `CGST (${gstRate * 50}%) + SGST (${gstRate * 50}%)`
                      : `IGST (${gstRate * 100}%)`}
                  </span>
                  <span>₹{gstAmount.toLocaleString("en-IN")}</span>
                </div>
              )}
              {gstRate === 0 && (
                <div className="flex justify-between text-slate-500 dark:text-slate-300 text-xs">
                  <span>GST</span><span>Not charged</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-slate-800 dark:text-white border-t border-amber-100 pt-2">
                <span>Total{gstType && gstRate > 0 ? " (incl. GST)" : ""}</span>
                <span className="text-amber-600">₹{grandTotal.toLocaleString("en-IN")}</span>
              </div>
            </div>
          )}

          {/* Guest details */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">YOUR DETAILS</p>
            {[
              { key: "guest_name", label: "Full Name", type: "text", placeholder: "Rohan Patil" },
              { key: "guest_email", label: "Email Address", type: "email", placeholder: "you@email.com" },
              { key: "guest_phone", label: "Phone Number", type: "tel", placeholder: "+91 98765 43210" },
            ].map(f => (
              <div key={f.key}>
                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">{f.label}</label>
                <input type={f.type} placeholder={f.placeholder} value={form[f.key]}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                  onBlur={() => (f.key === "guest_email" || f.key === "guest_phone") && saveLead("form_blur")}
                  className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20" />
                {f.key === "guest_phone" && (
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">
                    By providing your number you agree to receive booking updates (confirmation,
                    check-in details, reminders) on WhatsApp/SMS. Reply STOP anytime to opt out.
                  </p>
                )}
              </div>
            ))}

            {/* State selector — used to auto-determine intrastate vs interstate GST */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-slate-500 dark:text-slate-400">Your State <span className="text-slate-400">(for GST, optional)</span></label>
                <button type="button" onClick={detectState} disabled={detectingState}
                  className="flex items-center gap-1 text-[11px] text-amber-600 hover:text-amber-700 font-semibold disabled:opacity-50 transition-colors">
                  {detectingState
                    ? <><Spinner size={11} className="animate-spin" /> Detecting…</>
                    : <><NavigationArrow size={11} weight="fill" /> Auto-detect</>}
                </button>
              </div>
              <select value={form.guest_state}
                onChange={e => setForm(prev => ({ ...prev, guest_state: e.target.value }))}
                className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-100 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20">
                <option value="">Select your state (optional)</option>
                {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              {form.guest_state && property?.state && (
                <p className="text-[11px] mt-1 text-slate-400">
                  {form.guest_state.toLowerCase() === (property.state || "").toLowerCase()
                    ? "✓ Intrastate booking — CGST + SGST applies"
                    : "✓ Interstate booking — IGST applies"}
                </p>
              )}
            </div>

            {/* Corporate GST invoice — company name + GSTIN for a proper tax invoice */}
            <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 py-2.5 cursor-pointer">
              <input type="checkbox" checked={corp}
                onChange={e => setCorp(e.target.checked)}
                className="mt-0.5 accent-amber-500" />
              <span>
                <span className="block text-xs font-medium text-slate-700 dark:text-slate-200">
                  Booking for a company (need a GST invoice)
                </span>
                <span className="block text-[11px] text-slate-400 mt-0.5">
                  We'll add your company name and GSTIN to the invoice for reimbursement.
                </span>
              </span>
            </label>
            {corp && (
              <div className="space-y-2.5 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-900/10 p-3">
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-400">Company / Organisation name</label>
                  <input type="text" placeholder="e.g. Acme Technologies Pvt Ltd" value={form.bill_to_company}
                    onChange={e => setForm(prev => ({ ...prev, bill_to_company: e.target.value }))}
                    className="mt-1 w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-400">Company GSTIN <span className="text-slate-400">(optional)</span></label>
                  <input type="text" maxLength={15} placeholder="29ABCDE1234F1Z5" value={form.bill_to_gstin}
                    onChange={e => setForm(prev => ({ ...prev, bill_to_gstin: e.target.value.toUpperCase() }))}
                    className="mt-1 w-full px-3 py-2.5 text-sm font-mono rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-400">Billing address <span className="text-slate-400">(optional)</span></label>
                  <textarea rows={2} placeholder="Registered address for the invoice" value={form.bill_to_address}
                    onChange={e => setForm(prev => ({ ...prev, bill_to_address: e.target.value }))}
                    className="mt-1 w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20" />
                </div>
                <p className="text-[11px] text-slate-400">
                  You can download a proforma invoice on the next step, and the final tax invoice after payment.
                </p>
              </div>
            )}

            {/* Early check-in request */}
            <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 py-2.5 cursor-pointer">
              <input type="checkbox" checked={earlyCheckin}
                onChange={e => setEarlyCheckin(e.target.checked)}
                className="mt-0.5 accent-amber-500" />
              <span>
                <span className="block text-xs font-medium text-slate-700 dark:text-slate-200">
                  Request early check-in{property.checkin_time ? ` (before ${property.checkin_time})` : ""}
                </span>
                <span className="block text-[11px] text-slate-400 mt-0.5">
                  Subject to the host's approval — depends on the previous guest's checkout
                </span>
              </span>
            </label>
          </div>

          {/* Guest ID Upload */}
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <IdentificationCard size={14} className="text-slate-400" />
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                GOVERNMENT ID <span className="font-normal text-slate-400">(Aadhaar / Passport / Driver's License)</span>
              </label>
            </div>
            <label className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
              idFile ? "border-green-300 bg-green-50 dark:bg-green-900/20" : "border-slate-200 dark:border-slate-600 hover:border-amber-300 bg-slate-50 dark:bg-slate-700"
            }`}>
              <input type="file" accept="image/*,.pdf" className="hidden"
                onChange={e => handleIdUpload(e.target.files?.[0])} />
              {uploadingId ? (
                <><Spinner size={16} className="animate-spin text-amber-500" /><span className="text-xs text-slate-500">Uploading…</span></>
              ) : idFile ? (
                <><CheckFat size={16} weight="fill" className="text-green-500 shrink-0" /><span className="text-xs text-green-700 dark:text-green-400 truncate">{idFile.name}</span></>
              ) : (
                <><UploadSimple size={16} className="text-slate-400 shrink-0" /><span className="text-xs text-slate-400">Upload a photo or PDF of your ID</span></>
              )}
            </label>
            <p className="text-[10px] text-slate-400 mt-1">Stored securely · Shared only with your host for check-in verification</p>
          </div>

          {/* An ID for each of the other guests. Capped by the guest count chosen above,
              since the lead guest's own ID is the one collected just before this. */}
          {guests > 1 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                  OTHERS STAYING WITH YOU
                </label>
                <span className="text-[10px] text-slate-400">{partyIds.length} of {guests - 1}</span>
              </div>
              {partyIds.map((g, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <input value={g.name} onChange={e => patchPartyId(i, { name: e.target.value })}
                    placeholder="Guest name" data-testid={`party-name-${i}`}
                    className="flex-1 px-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-100 focus:outline-none focus:border-amber-400" />
                  <label className={`shrink-0 px-3 min-h-[44px] flex items-center text-xs font-semibold rounded-xl border-2 border-dashed cursor-pointer ${
                    g.url ? "border-green-300 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400"
                          : "border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-400"}`}>
                    <input type="file" accept="image/*,.pdf" className="hidden"
                      onChange={e => uploadPartyId(i, e.target.files?.[0])} />
                    {g.uploading ? "Uploading…" : g.url ? "ID added" : "Upload ID"}
                  </label>
                  <button type="button" onClick={() => removePartyId(i)} aria-label="Remove this guest"
                    className="shrink-0 min-h-[44px] px-2 text-slate-400 hover:text-red-500 text-sm">✕</button>
                </div>
              ))}
              {partyIds.length < guests - 1 && (
                <button type="button" onClick={addPartyId} data-testid="add-party-guest"
                  className="text-xs font-semibold text-amber-600 hover:underline min-h-[44px]">
                  + Add another guest
                </button>
              )}
              <p className="text-[10px] text-slate-400 mt-1">Optional, but your host may ask for an ID for every guest at check-in.</p>
            </div>
          )}

          {msg && (
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
              <Warning size={13} weight="fill" />{msg}
            </div>
          )}

          <button onClick={handleProceed} disabled={nights < 1 || submitting}
            className="w-full py-3.5 bg-amber-400 hover:bg-amber-500 text-white font-bold text-sm rounded-2xl transition disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm">
            {submitting
              ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              : <><ArrowRight size={16} weight="bold" />Review & Hold Dates<ArrowRight size={14} weight="bold" /></>
            }
          </button>

          <p className="text-[11px] text-slate-400 text-center">
            {upiOnly
              ? "Pay the host directly by UPI. Your booking is confirmed once they verify the payment."
              : "Secured by Razorpay, UPI, cards and net banking accepted"}
          </p>
          </div>
          )} {/* end stay tab */}

          {/* ── Event booking form ── */}
          {bookingTab === "event" && property.event_hosting_enabled && (
            <div className="p-6 space-y-5">
              <div>
                <h2 className="font-semibold text-slate-800 dark:text-white text-base">Book for an Event</h2>
                {property.event_available_hours && (
                  <p className="text-xs text-slate-400 mt-0.5">Available: {property.event_available_hours}</p>
                )}
              </div>

              {eventSuccess ? (
                <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                  className="text-center py-6">
                  <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle size={32} weight="fill" className="text-green-500" />
                  </div>
                  <h3 className="font-bold text-slate-800 dark:text-white text-lg mb-1">Request Sent!</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Your event booking request has been sent. The host will review and approve it — we'll notify you at <strong>{eventForm.organizer_email}</strong>.
                  </p>
                </motion.div>
              ) : (
                <>
                  {/* Event type */}
                  <div>
                    <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block">EVENT TYPE</label>
                    <select value={eventForm.event_type}
                      onChange={e => setEventForm(f => ({ ...f, event_type: e.target.value }))}
                      className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400">
                      <option value="">Select event type</option>
                      {(property.event_types || []).map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  {/* Event dates */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block">EVENT START DATE</label>
                      <input type="date" value={eventForm.event_start_date}
                        min={new Date().toISOString().split("T")[0]}
                        onChange={e => setEventForm(f => ({
                          ...f,
                          event_start_date: e.target.value,
                          event_end_date: f.event_end_date && f.event_end_date >= e.target.value ? f.event_end_date : e.target.value,
                        }))}
                        className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block">END DATE <span className="font-normal text-slate-400">(same day if 1-day)</span></label>
                      <input type="date" value={eventForm.event_end_date}
                        min={eventForm.event_start_date || new Date().toISOString().split("T")[0]}
                        onChange={e => setEventForm(f => ({ ...f, event_end_date: e.target.value }))}
                        className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400" />
                    </div>
                  </div>
                  {eventForm.event_start_date && (
                    <p className="text-[11px] text-slate-400 -mt-1">
                      Property blocked: {(() => {
                        const d = new Date(eventForm.event_start_date + "T00:00:00");
                        d.setDate(d.getDate() - 1);
                        const ci = d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
                        const co = new Date((eventForm.event_end_date || eventForm.event_start_date) + "T00:00:00")
                          .toLocaleDateString("en-IN", { day: "numeric", month: "short" });
                        return `${ci} (night before) → ${co} (event day evening)`;
                      })()}
                    </p>
                  )}

                  {/* Package price */}
                  {eventPackagePrice > 0 && (
                    <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 text-sm">
                      <div className="flex justify-between font-bold text-slate-800">
                        <span>Package price — {eventForm.event_type}</span>
                        <span className="text-amber-600">₹{eventPackagePrice.toLocaleString("en-IN")}</span>
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">Accommodation + event (night before + event day)</p>
                    </div>
                  )}

                  {/* Attendees */}
                  <div>
                    <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block">
                      NUMBER OF ATTENDEES {property.event_capacity ? `(max ${property.event_capacity})` : ""}
                    </label>
                    <input type="number" min="1" max={property.event_capacity || 999}
                      placeholder="e.g. 15"
                      value={eventForm.attendees}
                      onChange={e => setEventForm(f => ({ ...f, attendees: e.target.value }))}
                      className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400" />
                  </div>

                  {/* Organizer details */}
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">YOUR DETAILS</p>
                    {[
                      { key: "organizer_name",  label: "Full Name",     type: "text",  placeholder: "Priya Sharma" },
                      { key: "organizer_email", label: "Email Address", type: "email", placeholder: "you@email.com" },
                      { key: "organizer_phone", label: "Phone Number",  type: "tel",   placeholder: "+91 98765 43210" },
                    ].map(f => (
                      <div key={f.key}>
                        <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">{f.label}</label>
                        <input type={f.type} placeholder={f.placeholder} value={eventForm[f.key]}
                          onChange={e => setEventForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                          className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20" />
                      </div>
                    ))}
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block">NOTES / SPECIAL REQUIREMENTS</label>
                    <textarea rows={3} placeholder="Tell us about your event, setup needs, catering, etc."
                      value={eventForm.notes}
                      onChange={e => setEventForm(f => ({ ...f, notes: e.target.value }))}
                      className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400 resize-none" />
                  </div>

                  {property.event_setup_notes && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl p-3 text-xs text-blue-700 dark:text-blue-300">
                      <strong>Host notes:</strong> {property.event_setup_notes}
                    </div>
                  )}

                  {/* Guest ID upload */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <IdentificationCard size={14} className="text-slate-400" />
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                        GOVERNMENT ID <span className="font-normal text-slate-400">(Aadhaar / Passport / Driver's License)</span>
                      </label>
                    </div>
                    <label className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
                      eventIdFile ? "border-green-300 bg-green-50 dark:bg-green-900/20" : "border-slate-200 dark:border-slate-600 hover:border-amber-300 bg-slate-50 dark:bg-slate-700"
                    }`}>
                      <input type="file" accept="image/*,.pdf" className="hidden"
                        onChange={e => handleEventIdUpload(e.target.files?.[0])} />
                      {eventUploadingId ? (
                        <><Spinner size={16} className="animate-spin text-amber-500" /><span className="text-xs text-slate-500">Uploading…</span></>
                      ) : eventIdFile ? (
                        <><CheckFat size={16} weight="fill" className="text-green-500 shrink-0" /><span className="text-xs text-green-700 dark:text-green-400 truncate">{eventIdFile.name}</span></>
                      ) : (
                        <><UploadSimple size={16} className="text-slate-400 shrink-0" /><span className="text-xs text-slate-400">Upload a photo or PDF of your ID</span></>
                      )}
                    </label>
                    <p className="text-[10px] text-slate-400 mt-1">Stored securely · Shared only with your host for check-in verification</p>
                  </div>

                  {eventMsg && (
                    <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                      <Warning size={13} weight="fill" />{eventMsg}
                    </div>
                  )}

                  <button onClick={handleEventSubmit} disabled={eventSubmitting}
                    className="w-full py-3.5 bg-amber-400 hover:bg-amber-500 text-white font-bold text-sm rounded-2xl transition disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm">
                    {eventSubmitting
                      ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      : <><Star size={16} weight="fill" />Request Event Booking</>
                    }
                  </button>
                  <p className="text-[11px] text-slate-400 text-center">
                    No payment now — dates will be held once the host approves
                  </p>
                </>
              )}
            </div>
          )}
        </motion.div>
      </div>

      {/* Powered by footer */}
      <div className="text-center py-6 px-4">
        <a href="https://hostfolio.in" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-amber-500 transition">
          <span>Powered by</span>
          <span className="font-bold"><span className="text-slate-500">Host</span><span className="text-amber-500">Folio</span></span>
        </a>
        <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">Are you an Airbnb host?
          <a href="https://hostfolio.in/login" target="_blank" rel="noopener noreferrer"
            className="ml-1 text-amber-400 hover:underline font-medium">Try HostFolio free →</a>
        </p>
      </div>
    </div>
  );
}
